import React, { useEffect, useState } from 'react'
import { Button, WhatsAppButton } from '@olxbr/ds-core'
import { Container, Link, Divider, Skeleton } from '@olxbr/ds-web'
import styled from 'styled-components'
import Lurker from '../../../util/lurker'

import { ErrorAlertbox } from '../../atoms/ErrorAlertbox/ErrorAlertbox'
import { View } from '../../atoms/View'
import { breakpoints } from '../../../util/medias'
import { getMaskedValue, removeMask, replaceDDD } from '../../../util/PhonePattern'
import { Phone, MessageCircle } from '@olxbr/ds-icons'
import { userChallenges } from '../../../api/phoneValidationApiCall'
import cookie from '../../../util/cookie'
import { SelfServiceModal } from '../../organisms/SelfServiceModal'
import WaitLockBanner from '../../molecules/WaitLockBanner'
import { HiddenIn } from '../../atoms/HiddenIn'
import { getSendMode, SEND_MODES } from '../CheckPinCodeTemplate'

const PUBLIC_ACCOUNT_ID_EVENT_DEFAULT = 'no_public_account_id'

const DividerWrapper = styled.div`
  display: none;
  margin-top: var(--spacing-4);
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    display: inline;
  }
`

const ButtonsWrapper = styled.div`
  margin-top: var(--spacing-4);
  display: flex;
  justify-content: end;
  gap: var(--spacing-2);

  & > * {
    flex: 1;
  }
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    justify-content: center;
    flex-direction: column-reverse;
    align-items: stretch;
    flex: 1;

    & > * {
      width: 100%;
    }
  }
`

const Form = styled.form``

const ContentElement = ({ modalMode, ...props }) => {
  return modalMode ? <View {...props} /> : <Container {...props} />
}

const ContainerWrapper = styled(ContentElement)`
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    border: none;
    padding: 0;
  }
`

const StyledButton = styled(Button)`
  width: -webkit-fill-available;
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    margin: 0;
    width: 100%;
  }
`

const StyledText = styled.span.attrs({ className: 'typo-body-medium' })`
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    font-size: var(--font-size-1-75);
  }
`

const WrapperPhoneContainer = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  color: var(--color-neutral-130);
  background-color: var(--color-neutral-80);
  padding: var(--spacing-2);
  margin-bottom: var(--spacing-1);
  border-radius: var(--border-radius-1);
`
const HiddenInComponent = styled(HiddenIn)`
  width: -webkit-fill-available;
`

const Icon = styled.span`
  margin-right: var(--spacing-1);
`

const lurker = new Lurker('phone_2fa')

const USER_CHALLENGE_STATUS = {
  APPROVED: 'APPROVED',
  REFUSED: 'REFUSED',
  NOT_FOUND: 'NOT_FOUND',
  REQUESTED: 'REQUESTED',
  PENDING_AUTHORIZATION: 'PENDING_AUTHORIZATION',
  PENDING_SELFIE_REFERENCE: 'PENDING_SELFIE_REFERENCE',
  PENDING_SIDEEFFECTS: 'PENDING_SIDEEFFECTS',
  FINISHED: 'FINISHED',
  EXCLUDED: 'EXCLUDED',
}

export const SendPhone2FATemplate = ({
  onChooseAnotherMethodClick,
  onSuccess,
  lockTime,
  phone,
  token,
  redirectToVerifyYourAccess,
  onGetPublicAccountId,
  modalMode,
  sendPinCode,
  sendSMS,
  abTests,
}) => {
  const maskedPhone = getMaskedValue(removeMask(phone)).pretty

  const [errors, setErrors] = useState({
    showGenericError: false,
    showRateLimitError: false,
    showPhoneInUseError: false,
    showPhoneInBlackListError: false,
    showSfaBlockError: false,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showLink, setShowLink] = useState(false)
  const [isLoadingChallengeData, setIsLoadingChallengeData] = useState(true)
  const [hasRekogChallengeAvailable, setHasRekogChallengeAvailable] = useState(false)
  const [isModalOpen, setModalOpen] = useState(false)
  const [sfaErrorCode, setSfaErrorCode] = useState('')

  const isLocked = lockTime > 0
  const sfaDisplayCode = sfaErrorCode.split('_').slice(1).join('_')

  const showError = error => {
    setErrors(errors => ({ ...errors, [error]: true }))
    setIsSubmitting(false)
  }

  const handleConfirmChange = () => {
    lurker.clickEvt('self_service_2fa_button')

    setModalOpen(false)

    if (redirectToVerifyYourAccess) {
      redirectToVerifyYourAccess(hasRekogChallengeAvailable)
    }
  }

  const handleCancelChange = () => {
    lurker.clickEvt('self_service_2fa_modal_closed')
    setModalOpen(false)
  }

  const handleNoAccessLink = () => {
    lurker.clickEvt('self_service_2fa_modal_open')

    setModalOpen(true)
  }

  const setCookie = () => {
    const login_identifier_key = 'loginIdentifier'
    const expire_days = 30
    const date = new Date()
    date.setDate(date.getDate() + expire_days)

    cookie.create(login_identifier_key, token, date.toUTCString())
  }

  const onSubmit = type => {
    const flow = 'send_pincode'
    const sendMode = getSendMode(true)
    const eventType = type === 'WHATSAPP' ? 'whatsapp_button' : 'sms_button'
    lurker.clickEvt(eventType, {
      event_type: 'click',
      object_name: 'phone_2fa',
      type: sendMode,
      object_detail: eventType,
      public_account_id: onGetPublicAccountId,
    })
    sendPinCode(type, phone, token)
      .then(response => {
        lurker.viewMessageEvt('success', {
          flow,
          type: sendMode,
          event_type: 'message_view',
          object_name: 'phone_2fa',
          public_account_id: onGetPublicAccountId,
          method: type,
        })
        onSuccess(response)
      })
      .catch(error => {
        const statusCode = error?.response?.status
        const message = error?.response?.data?.message

        if (
          statusCode === 403 &&
          typeof message === 'string' &&
          message.includes('ERROR_KOHQXEDC9G')
        ) {
          setSfaErrorCode(message)
          showError('showSfaBlockError')
          lurker.viewMessageEvt('error', {
            accounts_reason: 'sfa_block',
            accounts_status_code: statusCode,
            accounts_error_message: message,
            flow,
            type: sendMode,
            event_type: 'message_view',
            object_name: 'phone_2fa',
            public_account_id: onGetPublicAccountId,
          })
        } else {
          showError('showGenericError')
          lurker.viewMessageEvt('error', {
            accounts_reason: 'rate_limit_exceeded',
            accounts_status_code: statusCode,
            accounts_error_message: message,
            flow,
            type: sendMode,
            event_type: 'message_view',
            object_name: 'phone_2fa',
            public_account_id: onGetPublicAccountId,
          })
        }
      })
  }

  const getUserChallenges = () => {
    userChallenges()
      .then(response => {
        const result = Object.values(response.data.challengesAvailability).some(Boolean)
        const { authorizationRequest: onGoingChallenge } = response.data

        setHasRekogChallengeAvailable(
          Boolean(response.data.challengesAvailability.REKOGNITION_AUTHENTICATION)
        )

        setShowLink(result)
        lurker.setPublicAccountId(
          response.data.userInfo?.publicAccountId ?? PUBLIC_ACCOUNT_ID_EVENT_DEFAULT
        )
        onGetPublicAccountId(response.data.userInfo?.publicAccountId)
        lurker.viewMessageEvt('self_service_2fa_button')

        if (
          [
            USER_CHALLENGE_STATUS.APPROVED,
            USER_CHALLENGE_STATUS.REFUSED,
            USER_CHALLENGE_STATUS.PENDING_AUTHORIZATION,
            USER_CHALLENGE_STATUS.PENDING_SELFIE_REFERENCE,
            USER_CHALLENGE_STATUS.PENDING_SIDEEFFECTS,
          ].includes(onGoingChallenge?.status)
        ) {
          redirectToVerifyYourAccess(
            !!response?.data?.challengesAvailability?.REKOGNITION_AUTHENTICATION
          )
        } else {
          setIsLoadingChallengeData(false)
        }
      })
      .catch(error => {
        setShowLink(false)
        setIsLoadingChallengeData(false)
        lurker.setPublicAccountId(
          error?.response?.data?.userInfo?.publicAccountId ?? PUBLIC_ACCOUNT_ID_EVENT_DEFAULT
        )
        lurker.errorMessageEvt('self_service_2fa_button')
      })
  }

  useEffect(() => {
    if (token && phone) {
      setCookie()
      getUserChallenges()
    } else {
      setIsLoadingChallengeData(false)
    }
  }, [token, phone])

  useEffect(() => {
    const sendMode = getSendMode(true)
    const loginMethod = globalThis.loginMethod || 'unknown'
    lurker.pageLoadEvt('phone_2fa', {
      flow: 'choose_pincode_method',
      type: sendMode,
      sent_from: loginMethod,
    })
  }, [])

  return (
    <Form>
      <div className="flex flex-col">
        <SelfServiceModal
          open={isModalOpen}
          onOpen={() => {}}
          onSuccessChange={handleConfirmChange}
          onCancelChange={handleCancelChange}
        />
        <ContainerWrapper
          type="outlined"
          paddingBottom="--spacing-8"
          paddingTop="--spacing-8"
          paddingLeft="--spacing-8"
          paddingRight="--spacing-8"
          modalMode={modalMode}
        >
          <div className="flex flex-col">
            <StyledText className="mb-4">
              Para garantir a segurança da sua conta,
              <b> enviaremos um código para o seu número. </b>
              Em caso de telefone fixo, ligaremos.
            </StyledText>
            {errors.showGenericError && (
              <ErrorAlertbox title="Falha em nosso sistema" showContactUs={false}>
                Estamos trabalhando pra resolver rapidinho. Tente novamente em alguns minutos.
              </ErrorAlertbox>
            )}
            {errors.showSfaBlockError && (
              <ErrorAlertbox title={`CodeError: ${sfaDisplayCode}`} showContactUs>
                Por favor, entre em contato antes de tentar novamente!
              </ErrorAlertbox>
            )}
            <WrapperPhoneContainer>
              <Phone />
              <span className="typo-body-medium ml-2">{replaceDDD(maskedPhone)}</span>
            </WrapperPhoneContainer>
            {isLoadingChallengeData && (
              <Skeleton marginTop="--spacing-0-5" height="30px" width="60%" />
            )}
            {showLink && (
              <div className="flex mt-0-5">
                <Link size="small" onClick={handleNoAccessLink}>
                  Meu número mudou, preciso alterar
                </Link>
              </div>
            )}
          </div>
        </ContainerWrapper>
        <DividerWrapper>
          <Divider />
        </DividerWrapper>
        {isLocked && (
          <WaitLockBanner
            value={lockTime}
            backgroundColor="var(--color-neutral-70)"
            justifyContent="flex-start"
            alignItem="end"
          />
        )}
        <ButtonsWrapper>
          <>
            <HiddenInComponent desktop tablet>
              {!modalMode && (
                <StyledButton
                  size="large"
                  variant="tertiary"
                  onClick={onChooseAnotherMethodClick}
                  disabled={isSubmitting}
                >
                  Voltar
                </StyledButton>
              )}
            </HiddenInComponent>
            <StyledButton
              size="large"
              disabled={isSubmitting || isLocked || isLoadingChallengeData}
              type="button"
              role="button"
              variant="tertiary"
              aria-label="Enviar código por SMS"
              onClick={() => onSubmit(SEND_MODES.PHONE)}
            >
              <Icon>
                <MessageCircle size="24" />
              </Icon>
              Receber por SMS
            </StyledButton>

            <WhatsAppButton
              size="large"
              disabled={isSubmitting || isLocked || isLoadingChallengeData}
              type="button"
              role="button"
              aria-label="Enviar código por WhatsApp"
              onClick={() => onSubmit(SEND_MODES.WHATSAPP)}
            >
              Receber por WhatsApp
            </WhatsAppButton>
          </>
        </ButtonsWrapper>
      </div>
    </Form>
  )
}
